/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * InviteClientModal - Modal for coaches to invite users as clients
 */

import { useState, useEffect } from 'react'
import { X, UserPlus, Users, Mail, Building2, AlertCircle, Loader2 } from 'lucide-react'
import coachService from '../services/coachService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('InviteClientModal')

const InviteClientModal = ({ isOpen, onClose, organizations, onInviteSent }) => {
  const [email, setEmail] = useState('')
  const [selectedOrg, setSelectedOrg] = useState('')
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState('')

  // Set default organization
  useEffect(() => {
    if (organizations?.length > 0 && !selectedOrg) {
      setSelectedOrg(organizations[0].id)
    }
  }, [organizations, selectedOrg])

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('')
      setError('')
    }
  }, [isOpen])

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleInvite = async (e) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    if (!selectedOrg) {
      setError('Please select an organization')
      return
    }

    setInviting(true)
    try {
      const response = await coachService.inviteClient(email.toLowerCase().trim(), selectedOrg)

      if (response.success) {
        toast.success(response.message || 'Invitation sent!')
        onInviteSent?.()
        onClose()
      }
    } catch (error) {
      logger.error('Error inviting client:', error)
      // coachService.inviteClient rethrows a plain Error whose message is the
      // extracted server message, so read error.message (not error.response).
      const message = error.message || 'Failed to send invitation'
      setError(message)
      toast.error(message)
    } finally {
      setInviting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Invite Client
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Send a coaching invitation to a user
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleInvite} className="p-6 space-y-4">
          {/* No organizations warning */}
          {(!organizations || organizations.length === 0) && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You need to be part of an organization to invite clients.
              </p>
            </div>
          )}

          {/* Organization selector */}
          {organizations?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                Organization
              </label>
              <select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="input w-full"
                disabled={inviting}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Your coaching organization for this relationship
              </p>
            </div>
          )}

          {/* Email input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Mail className="w-4 h-4 inline mr-1" />
              Client Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError('')
              }}
              placeholder="user@example.com"
              className="input w-full"
              autoFocus
              disabled={inviting || !organizations || organizations.length === 0}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Info note */}
          <div className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg">
            <p className="text-sm text-primary-800 dark:text-primary-200">
              <strong>Note:</strong> The user will receive a notification to accept your coaching
              invitation. If the user is a minor, their guardian will need to approve the request.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={inviting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2"
              disabled={inviting || !organizations || organizations.length === 0}
            >
              {inviting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InviteClientModal
